// ./src/components/generic/Logo/Logo.tsx
import React from "react";
import { Box, Text, Group } from "@mantine/core";
import { ASSETS } from "@utils/assets";

// ============================================
// Types
// ============================================

export type LogoSize = "small" | "medium" | "large";

export interface LogoProps {
  /** Size variant */
  size?: LogoSize;
  /** Show company name next to logo */
  withName?: boolean;
}

// ============================================
// Size Configuration
// ============================================

interface SizeConfig {
  height: number;
  fontSize: string;
  fontWeight: number;
  gap: "xs" | "sm" | "md";
}

const SIZE_CONFIG: Record<LogoSize, SizeConfig> = {
  small: {
    height: 28,
    fontSize: "1.3rem",
    fontWeight: 600,
    gap: "xs",
  },
  medium: {
    height: 36,
    fontSize: "1.25rem",
    fontWeight: 600,
    gap: "sm",
  },
  large: {
    height: 48,
    fontSize: "1.5rem",
    fontWeight: 700,
    gap: "md",
  },
};

// ============================================
// Component
// ============================================

export const Logo: React.FC<LogoProps> = ({
  size = "medium",
  withName = false,
}) => {
  const config = SIZE_CONFIG[size];

  const logoImage = (
    <Box
      component="img"
      src={ASSETS.LOGO}
      alt="FormSubmits"
      h={config.height}
      w="auto"
      style={{ objectFit: "contain" }}
    />
  );

  if (!withName) {
    return logoImage;
  }

  return (
    <Group gap={config.gap} align="center" wrap="nowrap">
      {logoImage}
      <Text
        fw={config.fontWeight}
        fz={config.fontSize}
        c="brand.6"
        lh={1.2}
        style={{
          letterSpacing: "-0.02em",
          fontFamily:
            "var(--mantine-font-family-headings, 'Space Grotesk', sans-serif)",
        }}
      >
        FormSubmits
      </Text>
    </Group>
  );
};
